// Pick the CLIENT ORDER a vendor invoice's goods were bought for.
//
// Deborah, 2026-09-04, asked for a dropdown "so you don't have to write every
// time", and — asked directly whether it should list the client or the vessel —
// chose the vessel: "from this client we could get a numerous number of vessels,
// same client". So the vessel is the LABEL, first and largest on every row.
//
// It is not what gets stored. She explained why herself in the same call: one
// client splits one ship's delivery across up to four purchase orders by ship
// department — provisions, cabin stores, safety stores, bonded — because the
// owner pays for some categories and the charterer for others, and "they need to
// have separation so they will know who to charge". Four orders, one ship, one
// vessel name. So this picks by the thing she recognises and hands back the
// order, whose id is what the invoice stores.
//
// Deliberately the same shape as `InvoiceSearchSelect` (the credit note's
// source-invoice picker) rather than a new pattern: opens on focus with the most
// recent orders so the list is browsable before you know what to type, says so
// while loading and when nothing matched, keyboard and outside-click, and a
// last-one-wins guard so a slow response cannot repopulate a list you have
// already moved past. Search breadth is the SERVER's (`listConfirmedOrders`) —
// vessel, client, RFQ number, quotation number and the client's own PO number —
// paged there too, because there are 50+ confirmed orders and that only grows.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ConfirmedOrderDto, fetchConfirmedOrders } from '../lib/confirmedOrderApi';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { clientDisplayName, orderRef } from '../utils/orderIdentity';
import { formatDate as fmtDate } from '../utils/dateFormat';

const RESULT_LIMIT = 8;

interface ConfirmedOrderSearchSelectProps {
  onSelect: (order: ConfirmedOrderDto) => void;
  /** The order already picked, so the row reads as chosen rather than offered. */
  selectedId?: string | null;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Focuses the search box the moment it mounts — item 49 (QA retest): this
   * box replaces a picked order's summary card the instant "Change" clears
   * it, and the reviewer who just pressed Change is about to type here next.
   */
  autoFocus?: boolean;
}

export const ConfirmedOrderSearchSelect: React.FC<ConfirmedOrderSearchSelectProps> = ({
  onSelect,
  selectedId = null,
  placeholder = 'Search by vessel, client, RFQ or the client’s PO number…',
  disabled = false,
  autoFocus = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ConfirmedOrderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const beginRequest = useLatestRequest();

  // An empty query is a real search: it asks for the most recent orders, which
  // is what makes the list browsable before you know what to type.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const isCurrent = beginRequest();
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await fetchConfirmedOrders(1, RESULT_LIMIT, query.trim(), true);
        if (cancelled || !isCurrent()) return;
        setResults(res.confirmedOrders || []);
        setFailed(false);
        setHighlight(0);
      } catch {
        // No server prose on screen (invariant 6) — and "we could not load
        // them" has to read differently from "there are none", or a permission
        // refusal looks like an empty database.
        if (!cancelled && isCurrent()) {
          setResults([]);
          setFailed(true);
        }
      } finally {
        // Guarded too: a superseded response clearing this would turn the
        // spinner off for a request that is still running.
        if (!cancelled && isCurrent()) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, open, beginRequest]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = useCallback(
    (order: ConfirmedOrderDto) => {
      onSelect(order);
      setQuery('');
      setOpen(false);
    },
    [onSelect]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const order = results[highlight];
      if (order) choose(order);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        value={query}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listboxId}
        className="w-full h-9 rounded-md border border-slate-200 px-3 text-xs outline-none focus:border-primary-500 disabled:bg-slate-50"
      />

      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {!query.trim() && !loading && results.length > 0 && (
            <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
              Most recent
            </div>
          )}

          {loading && <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>}

          {!loading && failed && (
            <div className="px-3 py-3 text-xs text-slate-500">
              Confirmed orders could not be loaded. You may not have access to that screen — ask an
              administrator, or try again.
            </div>
          )}

          {!loading && !failed && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {query.trim()
                ? `No confirmed order matches “${query.trim()}”. Try the vessel, the client, or the client’s PO number.`
                : 'No confirmed orders yet.'}
            </div>
          )}

          {!loading &&
            !failed &&
            results.map((order, i) => {
              const picked = selectedId === order._id;
              // The label owner, so this row and the Confirmed Order list say
              // the same thing about the same order.
              const ref = orderRef(order);
              const client = clientDisplayName(order.clientName) || clientDisplayName(order.emailFrom);
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  key={order._id}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => choose(order)}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 ${
                    picked ? 'bg-sky-50' : i === highlight ? 'bg-slate-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    {/* The vessel first, because that is what she recognises. */}
                    <span className="font-semibold text-slate-800">{order.vesselName || 'No vessel yet'}</span>
                    <span className="text-slate-500">{picked ? 'Linked' : ref || '--'}</span>
                  </div>
                  <div className="text-slate-500">
                    {client || '--'}
                    {order.clientPoNumber ? ` • Client PO ${order.clientPoNumber}` : ''}
                    {order.confirmedAt ? ` • ${fmtDate(order.confirmedAt, '')}` : ''}
                  </div>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default ConfirmedOrderSearchSelect;
